const article = {
	slug: "ascend-nj-networking-bingo",
	title: "A networking bingo app with no backend that still verifies its winners",
	date: "13 August 2026",
	updated: null,
	description:
		"How I built a fully client-side networking game for an Ascend NJ conference, including a booth verification step that works without any server.",
	keywords: ["pwa", "client-side app", "service worker", "event tech", "networking"],
	tldr:
		"I built a phone-based networking bingo game for an Ascend NJ conference. It runs entirely in the browser with no backend or accounts, captures contacts by scanning a QR badge, and still gives the prize booth a way to check that a win is real.",
	readingTime: null,
	provenance: "Written from my build notes, August 2026.",
	provenanceUrl: null,
	series: null,
	artifact: null,
	hero: {
		src: "/articles-media/bingo-board.png",
		alt: "A networking bingo board on a phone",
		caption: "The board. Each square is a prompt, and the glowing one is a move away from a win.",
	},
	markdown: `At an Ascend New Jersey event in July, the organizers ran a paper networking bingo. People liked it. Two things did not work. The prompts were too easy, so cards filled up without much real conversation. And winners were checked by hand, which meant a volunteer squinting at scribbled names.

For the September conference, I rebuilt it as a phone app. The goal was plain. Get strangers talking, make sure everyone leaves with real contacts, and let the booth confirm a win at a glance.

## No backend, on purpose

The whole app is one static HTML file. No server, no database, no accounts. Every board and every contact a player collects lives in that phone's local storage, and nothing is uploaded.

That constraint was a feature, not a limit. A conference network is crowded and flaky. A client-side app with a service worker keeps working when the wifi does not. There is no login to slow anyone down, and there is no attendee data sitting on a server for me or anyone else to worry about.

The tradeoff is that anything needing shared state, a live leaderboard for example, would need real infrastructure. For a one-time event, that was not worth it.

## Scanning beats typing

The first version made you tap a square and type a name. At a conference, nobody wants to type. So each player gets a personal QR code, their badge. When you meet someone who fits a square, you scan their code and the app pulls in their name and LinkedIn.

![Your personal QR. Another player scans it to add you to their board.](/articles-media/bingo-my-qr.png)

I flipped the order too. You scan first, then pick which square they match, because that is how the conversation actually goes. You meet a person, then work out where they fit.

![After a scan, the app asks which square the person matches.](/articles-media/bingo-scan-pick.png)

I used jsQR for the camera and a small generator for the badge, both loaded only when needed and cached for offline use. I also made a plain link work: if someone scans your badge with their phone camera instead of the app, the game opens and offers to add you anyway.

## Letting the booth trust a win, without a server

This was the part I cared about. On an honor-system game with no backend, how does the prize booth know a win is real and not five made-up names typed in twenty seconds?

I cannot prove honesty on a client-side app. But I can hand the booth signal. When a line completes, the win screen shows the people met, then a short integrity summary: how long the line took to fill, how many of the contacts have a LinkedIn on file, and a green or amber light. It also shows a verification code built from the player and the winning names and capture times, so the same board cannot be quietly cashed in twice.

![The winner screen. Below the contacts is the integrity summary and a verification code for the booth.](/articles-media/bingo-verify.png)

The code is a small hash computed on the phone. It is deterministic, so the order the contacts were added does not change it, and it is stable enough to compare against a booth's running list.

## Two touches that make it feel finished

When you win, you can make a card to share. It is drawn on a canvas in the browser, no image server involved, and it opens the phone's native share sheet.

![The share card, drawn in the browser and ready for the share sheet.](/articles-media/bingo-share-card.png)

It also installs. Proper icons, brand colors, and a quiet prompt to add it to the home screen. On the day, most people will just scan a QR and play in the browser, which is the right default. Install is there for anyone who wants an offline-proof icon.

![The add-to-home-screen prompt.](/articles-media/bingo-install.png)

## Testing a thing with no test framework

There was no test harness, and this was going into a room full of people at a live event, so I wrote one. I drove the running app over the browser's DevTools protocol and called its own functions, thirty-eight checks in all. Corrupt local storage recovers to a fresh board instead of a blank screen. Injected HTML in a name is escaped everywhere it shows. The contact export handles quotes and commas. Duplicate people are blocked whether the name differs by spacing, casing, or a messy LinkedIn URL.

They pass, which tells me the logic holds. What it does not tell me is whether a hundred people in a loud room find it fun. That test happens at the event.

## What is left

The honest answer is that the real proof is usage. The build is done and verified. Whether it gets people talking, and whether the booth flow holds up under a crowd, is what September will show. I will add the numbers here after.`,
};

export default article;
